import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { API_ROUTES, frontApi } from '_app/api';
import { components } from 'react-select';
import { Box, Divider, Flex, HStack, Text } from '@chakra-ui/react';
import { orderBy } from 'lodash';

import { FormProvider, useForm } from 'react-hook-form';
import { VStack } from '@chakra-ui/react';

import {
  NewRoomModalWrapper,
  RoomTypeSwitch,
  RoundTimeSelect,
  DiscordSwitch,
  // getTimeOptions,
  useLanguage,
  LangSelect,
  WinningPointsSelect,
  RoomLabel
} from 'components/NewRoomModal';

import { MultiSelect, Switch } from 'components/Form';

const POINTS_OPTIONS = [
  {
    value: 5,
    label: '5',
  },
  {
    value: 10,
    label: '10',
  },
  {
    value: 20,
    label: '20',
  },
  {
    value: 40,
    label: '40',
  },
];

const TIME_OPTIONS = [
  {
    value: 0.5 * 60,
    label: '30 sek',
  },
  {
    value: 1 * 60,
    label: '1 min',
  },
  {
    value: 2 * 60,
    label: '2 min',
  },
  {
    value: 5 * 60,
    label: '5 min',
  },
];

const CapNewRoomModal = ({
  show,
  handleClose,
  shortName = '',
}) => {
  // const [isPrivate, setIsPrivate] = useState(false);
  const { t } = useTranslation();

  const { activeLang, onActiveLangChange, languages } = useLanguage(shortName);

  const { data: decks } = useSWR(
    activeLang ? API_ROUTES.cap.decks(activeLang.value) : null,
  );

  const Option = ({
    data: { deckName, questionCards, answerCards, deckLevel },
    ...props
  }) => (
    <components.Option {...props}>
      <Flex
        justifyContent="space-between"
        px={4}
        py={2}
        cursor="pointer"
        _hover={{
          bg: '#edf2f7',
        }}
      >
        <Flex>
          <span>{deckName}</span>
          {'  '}
          <HStack spacing={0} ml={1}>
            {[...Array(deckLevel)].map((_, index) => (
              <img
                key={`${deckName}-lvl-${index}`}
                src="/images/games/cap/level-icon.png"
                alt=""
                width={12}
              />
            ))}
          </HStack>
        </Flex>
        <div>
          <span>
            {questionCards} / {answerCards}
          </span>
        </div>
      </Flex>
    </components.Option>
  );

  const methods = useForm({
    defaultValues: {
      roomType: 0,
      gameSettings: {
        roundTime: TIME_OPTIONS[1]?.value,
        winningPoints: POINTS_OPTIONS[1].value,
      },
    },
  });

  const { handleSubmit, watch, setValue } = methods;

  const onSubmit = async (values) => {
    console.log(values.roomId)

    const payload = {
      ...values,
      deckIds: values.decks.map(({ deckId }) => deckId),
      roomId: values.roomId || undefined
    }

    try {
      await frontApi.post(API_ROUTES.createRoom(shortName), payload);

      handleClose();
    } catch (error) {
      console.error(error);
    }

  };

  useEffect(() => {
    if (activeLang) {
      setValue('decks', []);
    }
  }, [activeLang]);

  const selectedDecks = watch('decks');

  return (
    <NewRoomModalWrapper
      {...{
        isOpen: show,
        onClose: handleClose,
        isDisabled: !selectedDecks || selectedDecks.length === 0,
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} id="new-room-form">
          <VStack spacing={4}>
            <MultiSelect
              label={
                <Flex alignItems="center">
                  <LangSelect
                    {...{ activeLang, onActiveLangChange, languages }}
                  />
                  <Text as="span" verticalAlign="middle" ml={1}>
                    {t('Decks')}
                  </Text>
                </Flex>
              }
              id="decks"
              name="decks"
              options={orderBy(decks, 'deckName', 'asc') || []}
              getOptionValue={(option) => option.deckId}
              components={{ Option }}
              formatOptionLabel={({ deckName }) => (
                <div>
                  <span>{deckName}</span>
                </div>
              )}
              isLoading={!!activeLang && !decks}
              isDisabled={!activeLang}
              placeholder={
                !activeLang
                  ? t('Select language first')
                  : `${t('Select one or more decks')}...`
              }
            />
            <Flex
              width="100%"
              sx={{
                gap: '16px',
              }}
              flexDir={{
                base: 'column',
                sm: 'row',
              }}
            >
              <WinningPointsSelect options={POINTS_OPTIONS} />
              <RoundTimeSelect
                options={TIME_OPTIONS}
                label={t('Answer time')}
              />
            </Flex>
            <RoomTypeSwitch />
          </VStack>
          <Divider my={3} />
          <VStack spacing={4}>
            <RoomLabel />
            <DiscordSwitch />
          </VStack>
          <Divider my={3} />
          <Box>
            <Switch name="gameSettings.bot" label="no bots / with bots" />
          </Box>
        </form>
      </FormProvider>
    </NewRoomModalWrapper>
  );
};

export default CapNewRoomModal;
